import React, { useRef, useState } from "react";
import styled from "styled-components";
import WarningIcon from "./assets/wAlert.svg";
import UserImage from "./assets/userIcon.png";
import CancelIcon from "./assets/wCancel.svg";
import useUserStore from "../stores/userStore";
import moment from "moment";
import { Indicator } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import fetchApi from "../components/Lib/api";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import Mixpanel from "../components/Lib/mixpanel";

const WarningBanner = styled.div`
  border-radius: 6px;
  border: 1px solid #ffd2d2;
  display: flex;
  padding: 10px 19px;
  margin-bottom: 20px;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  background: #fdecec;
  color: #815454;

  @media (max-width: 834px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 12px;
    font-size: 12px;
  }

  a {
    color: #3566fd;
    text-decoration: none;
    font-weight: bold;

    &:hover {
      color: #0056b3;
    }
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: nowrap;
  justify-content: flex-start;

  @media (max-width: 834px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
  }
`;

const Avatar = styled.img`
  border-radius: 10px;
  background: #ffd013;
  width: 100px;
  height: 100px;
  padding: 10px;

  @media (max-width: 834px) {
    width: 72px;
    height: 72px;
    padding: 6px;
  }
`;

const UserName = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  h3 {
    font-size: 24px;
    color: #000;
    margin-block: 8px;

    @media (max-width: 834px) {
      font-size: 16px;
      margin-block: 2px;
    }
  }

  p {
    font-size: 12px;
    color: #494949;
    width: 80ch;
    margin-block: 2px;

    @media (max-width: 834px) {
      width: 100%;
      font-size: 11px;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
`;

const UserHeader = () => {
  const fileInputRef = useRef(null);
  const [userImage, setUserImage] = useState(null);
  const { user } = useUserStore((state) => state.user);
  const { setUser } = useUserStore((state) => state.setUser);
  const [showWarning, setShowWarning] = useState(!user.profile_complete);
  const currentDate = moment().format("dddd, MMMM Do YYYY");
  const { t } = useTranslation();

  const handleFileChange = async (event) => {
    // Handle file changes here
    const selectedFile = event.target.files[0];
    setUserImage(URL.createObjectURL(selectedFile));


    Mixpanel.custom('Avatar Upload Started', {
      file_name: selectedFile.name,
      file_type: selectedFile.type,
      file_size_kb: Math.round(selectedFile.size / 1024),
      user_id: user.id
    });

    const startTime = performance.now();

    try {
      const formData = new FormData();
      formData.append(`user[avatar]`, selectedFile);
      const response = await fetchApi(
        `/api/users/update_image`,
        "PUT",
        formData,
        true
      );

      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      if (response.ok) {
        const responseData = await response.json();

        Mixpanel.content('User Avatar', 'Updated', {
          update_method: 'Direct Upload',
          file_name: selectedFile.name,
          file_size_kb: Math.round(selectedFile.size / 1024),
          response_time_ms: responseTime
      });
      } else {
        console.error("HTTP error! Failed to submit form");

        Mixpanel.error('Avatar Upload Error', {
          error_message: errorMessage,
          error_type: 'API Error',
          http_status: response.status,
          component: 'UserHeader',
          user_action: 'Avatar Upload',
          file_name: selectedFile.name,
          response_time_ms: responseTime
      });
      }
    } catch (error) {
      console.error("Error:", error.message);
      Mixpanel.error('Avatar Upload Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'UserHeader',
        user_action: 'Avatar Upload',
        file_name: selectedFile.name,
        response_time_ms: responseTime
      });
    }
  };

  return (
    <div>
      {showWarning && (
        <WarningBanner>
          <img src={WarningIcon} alt="Warning" />
         { t("user_header.warning")}
          <a href="/onboarding">Complete Profile Now</a>
          <img
            src={CancelIcon}
            alt="Close Warning"
            style={{ marginLeft: "auto", cursor: "pointer" }}
            onClick={() => setShowWarning(false)}
          />
        </WarningBanner>
      )}
      <UserProfile>
        <Indicator
          color="indigo"
          className="cursor-pointer"
          position="bottom-end"
          label={<IconPencil size={16} />}
          size={30}
          onClick={() => {
            fileInputRef.current.click();
            // Track: Edit Avatar Clicked
            Mixpanel.button('Edit Avatar Icon', {
              location: 'User Header',
              user_id: user.id,
              current_avatar_status: user.avatar_url ? 'exists' : 'default'
            });
          }}          
        >
          <Avatar
            src={userImage || user.avatar_url || UserImage}
            alt="User Avatar"
          />
        </Indicator>
        <UserName>
          <h3>{t("user_header.welcome")} {user.first_name}!</h3>
          <p>
           {t("user_header.message")}
          </p>
          <p>{currentDate}</p>
        </UserName>
      </UserProfile>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="image/*"
      />
    </div>
  );
};

export default UserHeader;